import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMarketContext } from '../context/MarketDataContext';
import { getStatusDotColor, getStatusDotStyle, getStatusTextColor, getStatusTextStyle } from '../utils/marketStatus';
import { getUserRole } from '../utils/auth';

function useLiveClocks() {
  const format = (date, timeZone) =>
    date.toLocaleString('en-US', {
      timeZone,
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });

  const [clocks, setClocks] = useState(() => {
    const now = new Date();
    return { us: format(now, 'America/New_York'), ksa: format(now, 'Asia/Riyadh') };
  });

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      setClocks({ us: format(now, 'America/New_York'), ksa: format(now, 'Asia/Riyadh') });
    };
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  return clocks;
}

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuRef = useRef(null);
  const { connected, marketStatus } = useMarketContext();
  const clocks = useLiveClocks();

  const isAdmin = getUserRole() === 'admin';

  const links = [
    { to: '/dashboard', label: 'Dashboard' },
    { to: '/short-and-long', label: 'Short & Long' },
    { to: '/sp500-momentum', label: 'S&P 500' },
  ];

  const monitorLinks = [
    { to: '/monitor/db', label: 'DB Monitor' },
    { to: '/monitor/server', label: 'Server Monitor' },
  ];

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMobileOpen(false);
      }
    };
    document.addEventListener('touchstart', handleClick);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('touchstart', handleClick);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [mobileOpen]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    navigate('/');
  };

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50" ref={menuRef}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16">
          {/* Logo */}
          <Link
            to="/dashboard"
            className="text-xl font-bold text-violet-600 tracking-tight py-2"
          >
            Tickerat
          </Link>

          {/* Desktop nav links */}
          <div className="hidden sm:flex items-center gap-1">
            {links.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive(link.to)
                    ? 'bg-violet-50 text-violet-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                {link.label}
              </Link>
            ))}
            {isAdmin && (
              <>
                <div className="w-px h-5 bg-gray-200 mx-1" />
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-2">Monitor</span>
                {monitorLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive(link.to)
                        ? 'bg-violet-50 text-violet-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </>
            )}
          </div>

          {/* Desktop: status + logout */}
          <div className="hidden sm:flex items-center gap-4">
            {/* Market status */}
            <div className="flex items-center gap-1.5">
              <span className={`inline-block w-2 h-2 rounded-full ${getStatusDotColor(marketStatus.color)}`} style={getStatusDotStyle(marketStatus.color)} />
              <span className={`text-xs font-medium ${getStatusTextColor(marketStatus.color)}`} style={getStatusTextStyle(marketStatus.color)}>
                {marketStatus.label}
              </span>
            </div>

            {/* Connection status */}
            <div className="flex items-center gap-1.5">
              <span className={`inline-block w-2 h-2 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'}`} />
              <span className="text-xs text-gray-500">
                {connected ? 'Live' : 'Offline'}
              </span>
            </div>

            <div className="w-px h-5 bg-gray-200" />

            {/* US & KSA clocks */}
            <div className="flex items-center gap-3 text-xs tabular-nums">
              <div className="flex items-center gap-1.5">
                <span className="font-medium text-gray-400">US</span>
                <span className="text-gray-600">{clocks.us}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="font-medium text-gray-400">KSA</span>
                <span className="text-gray-600">{clocks.ksa}</span>
              </div>
            </div>

            <div className="w-px h-5 bg-gray-200" />

            <button
              onClick={handleLogout}
              className="text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors py-2 px-3"
            >
              Logout
            </button>
          </div>

          {/* Mobile: status indicators + menu button */}
          <div className="sm:hidden flex items-center gap-3">
            {/* Mobile status badges + clocks */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${getStatusDotColor(marketStatus.color)}`} style={getStatusDotStyle(marketStatus.color)} />
                <span className={`text-[10px] font-medium ${getStatusTextColor(marketStatus.color)}`} style={getStatusTextStyle(marketStatus.color)}>
                  {marketStatus.label}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className="text-[10px] text-gray-500">
                  {connected ? 'Live' : 'Offline'}
                </span>
              </div>
              <div className="text-[10px] tabular-nums text-gray-500">
                <span className="font-medium text-gray-400">US</span> {clocks.us}
              </div>
              <div className="text-[10px] tabular-nums text-gray-500">
                <span className="font-medium text-gray-400">KSA</span> {clocks.ksa}
              </div>
            </div>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="flex items-center justify-center w-11 h-11 rounded-lg text-gray-500 hover:bg-gray-50 active:bg-gray-100"
              aria-label="Toggle menu"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {mobileOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="sm:hidden border-t border-gray-200 bg-white shadow-lg">
          <div className="px-4 py-2 space-y-1">
            {links.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`block px-4 py-3.5 rounded-lg text-base font-medium transition-colors active:bg-gray-100 ${
                  isActive(link.to)
                    ? 'bg-violet-50 text-violet-700'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {link.label}
              </Link>
            ))}
            {isAdmin && (
              <div className="border-t border-gray-100 mt-1 pt-1">
                <div className="px-4 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Monitor</div>
                {monitorLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`block px-4 py-3.5 rounded-lg text-base font-medium transition-colors active:bg-gray-100 ${
                      isActive(link.to)
                        ? 'bg-violet-50 text-violet-700'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}
            <div className="border-t border-gray-100 mt-1 pt-1">
              <button
                onClick={handleLogout}
                className="w-full text-left px-4 py-3.5 rounded-lg text-base text-red-500 hover:bg-red-50 active:bg-red-100 font-medium"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
